import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Avatar, Typography } from '@mui/material';
import { useGetProductRelations } from '@queryHooks/useProductRelations';
import { useGetSoftwareList } from '@queryHooks/useSoftware';
import type { SoftwareModel } from '@models/softwareModels';
import { ProductRelationDetailModel } from '@repositories/productsRelationsRepository/models/ProductRelationDetailModel';
import * as S from './ProductDetailRelatedProductsStyles';

// 프로바이더 스토어의 접근 가능 상품 조회 시 최대 크기
// 추후 백엔드 API에서 product-relations에 providerDomain 필터를 지원하면 이 방식을 대체할 것
const MAX_PROVIDER_SOFTWARES_SIZE = 1000;

interface Props {
  software: SoftwareModel;
}

export const ProductDetailRelatedProducts = ({ software }: Props) => {
  const { t } = useTranslation();
  const { providerDomain } = useParams<{ providerDomain: string }>();
  const { data: productRelations } = useGetProductRelations(software.id ?? '');
  const { data: softwaresInCategory } = useGetSoftwareList({
    category: software.categories?.map(category => category.id),
    size: 6,
    providerDomain,
  });

  // 프로바이더 스토어 접근 시 접근 가능한 전체 상품 목록 조회
  const { data: providerSoftwares, isSuccess: isProviderSoftwaresLoaded } = useGetSoftwareList(
    { providerDomain, size: MAX_PROVIDER_SOFTWARES_SIZE },
    { enabled: !!providerDomain },
  );

  // 프로바이더 스토어에서 접근 가능한 상품 ID Set
  const accessibleProductIds = useMemo(() => {
    if (!providerDomain || !isProviderSoftwaresLoaded) return null;
    return new Set(providerSoftwares.content.map(sw => sw.id));
  }, [providerDomain, isProviderSoftwaresLoaded, providerSoftwares]);

  let relatedProducts = productRelations.filter(product => product.type === 'RELATED');
  let recommendProducts = productRelations.filter(product => product.type === 'RECOMMEND');

  // 프로바이더 스토어 접근 시 productRelations 기반 상품만 접근 가능 목록으로 필터링
  if (accessibleProductIds) {
    relatedProducts = relatedProducts.filter(product => accessibleProductIds.has(product.productId));
    recommendProducts = recommendProducts.filter(product => accessibleProductIds.has(product.productId));
  }

  // NOTE. 없으면 같은 카테고리의 제품 (softwaresInCategory는 이미 providerDomain 범위로 조회됨)
  if (relatedProducts.length === 0 && softwaresInCategory.totalElements > 0) {
    relatedProducts = softwaresInCategory.content
      .filter(product => product.id !== software.id)
      .map(
        product =>
          new ProductRelationDetailModel({
            productId: product.id,
            name: product.name,
            categoryName: product.category?.name,
            description: product.description,
            iconUrl: product.iconUrl,
            type: 'RELATED',
          }),
      );
  }

  // NOTE. 컴포넌트를 만들지 않아야 함. (null)
  if (relatedProducts.length === 0 && recommendProducts.length === 0) return null;

  return (
    <S.RelatedAppsWapper>
      <S.RelatedAppsCategory>
        <Typography variant="headline8" component="strong">
          {t('Ad_Store_Product_Detail_03')}
        </Typography>
        <S.RelatedAppsList>
          {relatedProducts.length > 0 ? (
            relatedProducts.map(product => <RelatedProduct key={product.productId} product={product} />)
          ) : (
            <Typography>{t('Product_Detail_Related.None')}</Typography>
          )}
        </S.RelatedAppsList>
      </S.RelatedAppsCategory>
      {recommendProducts.length > 0 && (
        <S.RelatedAppsCategory>
          <Typography variant="headline8" component="strong">
            {t('Ad_Store_Product_Detail_04')}
          </Typography>
          <S.RelatedAppsList>
            {recommendProducts.map(product => (
              <RelatedProduct key={product.productId} product={product} />
            ))}
          </S.RelatedAppsList>
        </S.RelatedAppsCategory>
      )}
    </S.RelatedAppsWapper>
  );
};

interface ProductRelationProps {
  product: ProductRelationDetailModel;
}

const RelatedProduct = ({ product }: ProductRelationProps) => {
  return (
    <li>
      <S.RelatedAppWrapper to={`../product/${product.productId}`}>
        {product.categoryName ? <S.RelatedAppCategory>{product.categoryName}</S.RelatedAppCategory> : null}
        <S.AppWrapper>
          <Avatar sx={{ width: '24px', height: '24px', borderRadius: 0 }} src={product.iconUrl} />
          <S.AppInfoWrapper>
            {product.name ? (
              <Typography variant="body2" component="p">
                {product.name}
              </Typography>
            ) : null}
            {product.description ? (
              <S.RelatedAppDescrpition variant="paragraph4" component="p" noWrap>
                {product.description}
              </S.RelatedAppDescrpition>
            ) : null}
          </S.AppInfoWrapper>
        </S.AppWrapper>
      </S.RelatedAppWrapper>
    </li>
  );
};
